import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import {
  BucketAsset,
  BucketNumberEnum,
  useListBucketsQuery,
} from "features/trading/buckets";
import { sortMembers } from "./constants";

export const ALL_BUCKETS = "all";
// Buckets 1 and 2 share one account, so they are one scope here.
export const BUCKETS_1_2 = "1_2";

export type BucketScope =
  | typeof ALL_BUCKETS
  | typeof BUCKETS_1_2
  | BucketNumberEnum.BUCKET_3;

const ASSET_BUCKET_NUMBERS = [
  BucketNumberEnum.BUCKET_1,
  BucketNumberEnum.BUCKET_2,
];

export interface BucketAssetSelection {
  assetIds: number[];
  // Whether the user narrowed the assets by hand, rather than leaving the
  // dropdown empty for every asset in scope.
  narrowed: boolean;
}

interface BucketAssetSelectorProps {
  accountId?: number | null;
  showBucket3: boolean;
  scope: BucketScope;
  // Not called until the buckets have loaded: before that there is no
  // selection to report.
  onSelectionChange: (selection: BucketAssetSelection) => void;
  onScopeChange: (scope: BucketScope) => void;
}

/**
 * Pick All, Bucket 1 & 2 or Bucket 3 - then optionally narrow to specific
 * Bucket 1/2 assets. The asset options are the buckets' own assetsInScope,
 * already loaded alongside the bucket, so this does not search the global
 * asset catalogue
 */
export const BucketAssetSelector = ({
  accountId,
  showBucket3,
  scope,
  onSelectionChange,
  onScopeChange,
}: BucketAssetSelectorProps) => {
  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);

  const { data: bucketsData } = useListBucketsQuery(
    { account: accountId as number },
    { skip: !accountId },
  );

  // Both buckets' included assets - the same asset can sit in both, so it
  // is taken once.
  const bucketAssets = useMemo(() => {
    const unique: BucketAsset[] = [];
    ASSET_BUCKET_NUMBERS.forEach((number) => {
      bucketsData?.results
        .find((bucket) => bucket.number === number)
        ?.assets.filter(({ included }) => included)
        .forEach((bucketAsset) => {
          if (!unique.some(({ asset }) => asset === bucketAsset.asset)) {
            unique.push(bucketAsset);
          }
        });
    });
    return sortMembers(unique);
  }, [bucketsData]);

  // Bucket 3 is a scope of its account's own rows - no asset to narrow.
  const isBucket3 = scope === BucketNumberEnum.BUCKET_3;

  const scopeAssets = useCallback(
    (target: BucketScope) => {
      const inScope = target === BucketNumberEnum.BUCKET_3 ? [] : bucketAssets;
      const picked = inScope.filter(({ asset }) =>
        selectedAssetIds.includes(asset),
      );
      return {
        inScope,
        selection: {
          narrowed: picked.length > 0,
          assetIds: (picked.length ? picked : inScope).map(
            ({ asset }) => asset,
          ),
        },
      };
    },
    [bucketAssets, selectedAssetIds],
  );

  const { inScope: assetsInScope, selection } = useMemo(
    () => scopeAssets(scope),
    [scopeAssets, scope],
  );

  const selectedAssets = useMemo(
    () => bucketAssets.filter(({ asset }) => selectedAssetIds.includes(asset)),
    [bucketAssets, selectedAssetIds],
  );

  const isLoaded = Boolean(bucketsData);

  useEffect(() => {
    if (isLoaded) onSelectionChange(selection);
  }, [onSelectionChange, selection, isLoaded]);

  const handleScopeChange = (_: unknown, value: BucketScope | null) => {
    if (value === null) return;
    onScopeChange(value);
    if (isLoaded) onSelectionChange(scopeAssets(value).selection);
  };

  const assetCountChip = (target: BucketScope) => (
    <Chip
      size="small"
      label={bucketAssets.length}
      color={scope === target ? "primary" : "default"}
    />
  );

  const buttonSx = { paddingX: 2, gap: 1, textTransform: "none" };

  const handleAssetsChange = (_: unknown, value: BucketAsset[]) => {
    setSelectedAssetIds(value.map(({ asset }) => asset));
  };

  return (
    <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={scope}
        onChange={handleScopeChange}
      >
        <ToggleButton value={ALL_BUCKETS} sx={buttonSx}>
          All
          {assetCountChip(ALL_BUCKETS)}
        </ToggleButton>
        <ToggleButton value={BUCKETS_1_2} sx={buttonSx}>
          Bucket 1 &amp; 2
          {assetCountChip(BUCKETS_1_2)}
        </ToggleButton>
        {showBucket3 && (
          <ToggleButton value={BucketNumberEnum.BUCKET_3} sx={buttonSx}>
            Bucket 3
          </ToggleButton>
        )}
      </ToggleButtonGroup>
      {isBucket3 ? null : (
        <Autocomplete<BucketAsset, true>
          multiple
          size="small"
          options={assetsInScope}
          value={selectedAssets}
          onChange={handleAssetsChange}
          getOptionLabel={({ symbol, asset__name }) =>
            `${symbol} - ${asset__name}`
          }
          isOptionEqualToValue={(option, value) => option.asset === value.asset}
          sx={{ flexGrow: 1, minWidth: 280 }}
          noOptionsText="No assets in scope"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Assets"
              placeholder={selectedAssets.length ? "" : "All assets in scope"}
            />
          )}
        />
      )}
    </Box>
  );
};

export default BucketAssetSelector;
